import { useRef, useState, useEffect } from 'react';

const REASONS = [
  {
    icon: 'ri-focus-3-line',
    title: 'Precision-Driven Execution',
    desc: 'Every system is engineered with exacting tolerances. We build what we promise and promise only what we can deliver — with full confidence.',
    stat: null,
  },
  {
    icon: 'ri-award-line',
    title: '25+ Years of Industry Expertise',
    desc: 'Decades of application knowledge across automotive, aerospace, military, mining, and heavy industrial markets. Experience that changes how problems get solved.',
    stat: '25+',
  },
  {
    icon: 'ri-flag-line',
    title: 'American-Built MAXJET Systems',
    desc: 'MAXJET systems built in the USA with domestic materials ensuring long-term durability and guaranteed domestic part availability — no supply chain uncertainty.',
    stat: null,
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Fully Custom Configurations',
    desc: 'We design systems around your specific parts, production volumes, and facility layout. No forced-fit equipment. No compromise on performance.',
    stat: null,
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Long-Term Support Partnerships',
    desc: 'Direct technical support, service access, and parts availability long after the sale. We stand behind every system we build, permanently.',
    stat: null,
  },
];

export default function WhyChooseUs() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="relative bg-[#0a0a0a] py-32 overflow-hidden" ref={ref}>

      {/* Background image — very dark overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/55a8a5bdedd6964a61387ef4ae6eb627.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0a0a0a]/94" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-start">

          {/* Left: Layered image treatment */}
          <div className={`relative transition-all duration-800 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>

            {/* Decorative corner frames */}
            <div className="absolute -top-8 -left-8 w-40 h-40 border border-[#c9a84c]/8 rounded-2xl pointer-events-none" />
            <div className="absolute -bottom-8 -right-8 w-56 h-56 border border-[#c9a84c]/5 rounded-2xl pointer-events-none" />
            <div className="absolute -top-4 -left-4 w-20 h-20 border border-[#c9a84c]/15 rounded-xl pointer-events-none" />

            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden border border-[#1c1c1c]">
              <img
                src="https://readdy.ai/api/search-image?query=large%20industrial%20conveyor%20belt%20parts%20washing%20machine%20in%20manufacturing%20facility%2C%20automated%20parts%20cleaning%20system%20in%20operation%2C%20professional%20factory%20setting%2C%20dramatic%20industrial%20lighting%20from%20overhead%2C%20close-up%20precision%20machinery%2C%20warm%20metallic%20amber%20tones&width=700&height=560&seq=why-main-v4&orientation=portrait"
                alt="KC Quality Systems industrial conveyor washing system"
                className="w-full h-[500px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/15 to-transparent" />

              {/* Floating overlay stats */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { num: '25+', label: 'Years', icon: 'ri-time-line' },
                    { num: '100%', label: 'USA Built', icon: 'ri-flag-line' },
                    { num: '12+', label: 'Industries', icon: 'ri-building-line' },
                  ].map((s, i) => (
                    <div key={i} className="text-center px-3 py-4 bg-black/70 backdrop-blur-md border border-[#c9a84c]/12 rounded-xl hover:border-[#c9a84c]/30 transition-colors">
                      <div className="w-6 h-6 flex items-center justify-center mx-auto mb-2">
                        <i className={`${s.icon} text-[#c9a84c] text-sm`} />
                      </div>
                      <div className="font-serif text-2xl text-[#c9a84c] font-bold leading-none mb-1">{s.num}</div>
                      <p className="text-white/40 text-[10px] font-sans tracking-wide">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Side accent badge */}
            <div className="absolute -right-6 top-1/3 transform -translate-y-1/2 hidden xl:flex flex-col items-center gap-2 bg-[#0d0d0d] border border-[#c9a84c]/20 rounded-xl p-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/20">
                <i className="ri-medal-line text-[#c9a84c]" />
              </div>
              <div className="h-px w-10 bg-[#c9a84c]/20" />
              <div className="writing-mode-vertical text-[10px] text-white/30 font-sans tracking-widest uppercase" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                American Made
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className={`transition-all duration-800 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <span className="inline-flex items-center gap-3 text-[#c9a84c] text-[10px] font-bold tracking-[0.5em] uppercase font-sans">
              <span className="h-px w-8 bg-[#c9a84c]" />
              Why KC Quality Systems
            </span>
            <h2 className="font-serif text-5xl lg:text-[3.5rem] text-white mt-6 mb-5 leading-[1.05]">
              Engineered for Performance.
              <br />
              <em className="not-italic text-[#c9a84c]">Built for Longevity.</em>
            </h2>
            <p className="text-white/40 font-sans text-base leading-relaxed mb-10">
              Every system we build is a direct reflection of our commitment to precision, durability, and long-term operational value. We don&apos;t just deliver equipment — we deliver confidence.
            </p>

            <div className="space-y-4 mb-12">
              {REASONS.map((r, idx) => (
                <div
                  key={idx}
                  className={`flex gap-4 p-5 rounded-xl border border-[#1c1c1c] hover:border-[#c9a84c]/20 hover:bg-[#0d0d0d] transition-all duration-400 group ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                  style={{ transitionDelay: `${400 + idx * 80}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl bg-[#c9a84c]/8 border border-[#c9a84c]/15 group-hover:bg-[#c9a84c]/14 transition-colors">
                    <i className={`${r.icon} text-[#c9a84c] text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-sans font-bold text-white text-sm">{r.title}</h4>
                      {r.stat && (
                        <span className="px-2 py-0.5 bg-[#c9a84c]/10 border border-[#c9a84c]/20 rounded-full text-[#c9a84c] text-[10px] font-bold font-sans whitespace-nowrap">
                          {r.stat} yrs
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-sm font-sans leading-relaxed">{r.desc}</p>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-s-line text-[#c9a84c]" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="group px-8 py-4 bg-[#c9a84c] text-black font-bold text-sm rounded-xl hover:bg-[#d4b55a] transition-all duration-300 cursor-pointer font-sans whitespace-nowrap flex items-center gap-3"
              >
                Request a Consultation
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://www.kc-qualitysystems.com/custom-washer-quote/"
                className="px-8 py-4 border border-[#2a2a2a] text-white/55 font-semibold text-sm rounded-xl hover:border-[#c9a84c]/40 hover:text-[#c9a84c] transition-colors cursor-pointer font-sans whitespace-nowrap"
              >
                Get a Custom Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
